import { useAdminStats } from "../../../lib/api/admin.api";

const STATUS_LABELS: Record<string, string> = {
  todo: "À faire",
  doing: "En cours",
  done: "Fait",
};

const CATEGORY_LABELS: Record<string, string> = {
  spiritual: "Spirituel",
  personal: "Personnel",
  professional: "Professionnel",
};

const STATUS_COLORS: Record<string, string> = {
  todo: "bg-gray-400",
  doing: "bg-amber-500",
  done: "bg-green-500",
};

function StatCard({ label, value, sub }: { readonly label: string; readonly value: number; readonly sub?: string }) {
  return (
    <div className="bg-surface-alt rounded-xl p-3 flex flex-col gap-1">
      <span className="text-2xl font-bold text-ink">{value}</span>
      <span className="text-xs text-ink-muted">{label}</span>
      {sub && <span className="text-[10px] text-ink-faint">{sub}</span>}
    </div>
  );
}

function SignupsChart({ data }: { readonly data: Array<{ date: string; count: number }> }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="bg-surface-alt rounded-xl p-3">
      <span className="text-xs text-ink-muted block mb-3">Inscriptions (7 derniers jours)</span>
      <div className="flex items-end gap-1.5 h-16">
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[9px] text-ink-faint font-medium">{d.count}</span>
            <div
              className="w-full bg-accent/80 rounded-t transition-all"
              style={{ height: `${Math.max(4, (d.count / max) * 100)}%` }}
            />
            <span className="text-[8px] text-ink-faint">
              {new Date(d.date).toLocaleDateString("fr-FR", { weekday: "short" }).slice(0, 3)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminStatsTab() {
  const { data: stats, isLoading } = useAdminStats();

  if (isLoading) return <p className="text-ink-muted text-sm">Chargement...</p>;
  if (!stats) return <p className="text-ink-muted text-sm">Aucune donnée</p>;

  const total = stats.taskCount + stats.eventCount + stats.habitCount;
  const authTotal = stats.authDistribution.email + stats.authDistribution.google + stats.authDistribution.github;

  return (
    <div className="flex flex-col gap-6">
      {/* Compteurs globaux */}
      <section>
        <h3 className="text-sm font-semibold text-ink mb-3">Vue d'ensemble</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard label="Utilisateurs" value={stats.userCount} />
          <StatCard label="Tâches" value={stats.taskCount} />
          <StatCard label="Événements" value={stats.eventCount} />
          <StatCard label="Habitudes" value={stats.habitCount} />
          <StatCard label="Total" value={total} />
        </div>
      </section>

      {/* Activité + Inscriptions */}
      <section>
        <h3 className="text-sm font-semibold text-ink mb-3">Activité</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard label="Actifs aujourd'hui" value={stats.activeUsersToday} />
          <StatCard label="Actifs cette semaine" value={stats.activeUsersThisWeek} />
          <div className="bg-surface-alt rounded-xl p-3">
            <span className="text-xs text-ink-muted block mb-2">Méthodes d'auth</span>
            <div className="flex flex-col gap-1.5 text-xs">
              {[
                { label: "Email", count: stats.authDistribution.email },
                { label: "Google", count: stats.authDistribution.google },
                { label: "GitHub", count: stats.authDistribution.github },
              ].map((m) => (
                <div key={m.label} className="flex items-center gap-2">
                  <span className="text-ink flex-1">{m.label}</span>
                  <div className="w-16 h-1.5 bg-border rounded-full overflow-hidden">
                    <div className="h-full bg-accent rounded-full" style={{ width: `${authTotal > 0 ? (m.count / authTotal) * 100 : 0}%` }} />
                  </div>
                  <span className="text-ink-muted font-medium w-6 text-right">{m.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Graphe inscriptions */}
      {stats.signupsPerDay.length > 0 && <SignupsChart data={stats.signupsPerDay} />}

      {/* Répartitions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-surface-alt rounded-xl p-3">
          <span className="text-xs text-ink-muted block mb-2">Tâches par statut</span>
          <div className="flex flex-col gap-1.5 text-xs">
            {stats.tasksByStatus.map((s) => (
              <div key={s.status} className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_COLORS[s.status] ?? "bg-gray-400"}`} />
                <span className="text-ink flex-1">{STATUS_LABELS[s.status] ?? s.status}</span>
                <span className="text-ink-muted font-medium">{s._count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface-alt rounded-xl p-3">
          <span className="text-xs text-ink-muted block mb-2">Tâches par catégorie</span>
          <div className="flex flex-col gap-1.5 text-xs">
            {stats.tasksByCategory.map((c) => (
              <div key={c.category} className="flex items-center justify-between">
                <span className="text-ink">{CATEGORY_LABELS[c.category] ?? c.category}</span>
                <span className="text-ink-muted font-medium">{c._count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface-alt rounded-xl p-3">
          <span className="text-xs text-ink-muted block mb-2">Répartition</span>
          <div className="flex flex-col gap-1.5 text-xs">
            {[
              { label: "Tâches", pct: total > 0 ? Math.round((stats.taskCount / total) * 100) : 0 },
              { label: "Événements", pct: total > 0 ? Math.round((stats.eventCount / total) * 100) : 0 },
              { label: "Habitudes", pct: total > 0 ? Math.round((stats.habitCount / total) * 100) : 0 },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <span className="text-ink flex-1">{item.label}</span>
                <div className="w-16 h-1.5 bg-border rounded-full overflow-hidden">
                  <div className="h-full bg-accent rounded-full" style={{ width: `${item.pct}%` }} />
                </div>
                <span className="text-ink-muted font-medium w-8 text-right">{item.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
